import React, { useState, useEffect, useRef } from 'react';
import { 
  Bot, 
  MessageCircle, 
  History,
  Trash2,
  Lightbulb,
  TrendingUp,
  BarChart3,
  Users,
  Package,
  DollarSign,
  AlertCircle,
  X,
  Maximize2,
  Minimize2,
  Send,
  Plus,
  Search,
  Copy,
  Check,
  Volume2,
  VolumeX,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Menu,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import firebaseAIService from '../../services/firebaseAIService';
import businessIntelligenceService from '../../services/businessIntelligenceService';
import { dataService } from '../../services/dataService';
import { ChatSession, ChatMessage } from '../../services/dataService';

// Type declarations for speech recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface AIChatbotProps {}

const AIChatbot: React.FC<AIChatbotProps> = () => {
  const { tenantData } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [showSessions, setShowSessions] = useState(true); // Default open on desktop
  const [searchQuery, setSearchQuery] = useState('');
  
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [hasAccess, setHasAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);

  // Advanced features
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check AI access
  useEffect(() => {
    const checkAccess = async () => {
      if (!tenantData?.id) return;
      
      try {
        setAccessLoading(true);
        const access = await dataService.hasAiChatbotAccess(tenantData.id);
        setHasAccess(access);
      } catch (error) {
        console.error('Error checking AI access:', error);
        setHasAccess(false);
      } finally {
        setAccessLoading(false);
      }
    };

    checkAccess();
  }, [tenantData?.id]);

  // Load chat sessions
  useEffect(() => {
    const loadChatSessions = async () => {
      if (!tenantData?.id) return;
      
      try {
        setIsLoadingSessions(true);
        const sessions = await dataService.getChatSessions(tenantData.id);
        setChatSessions(sessions);
      } catch (error) {
        console.error('Error loading chat sessions:', error);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadChatSessions();
  }, [tenantData?.id]);

  // Initialize chat session
  useEffect(() => {
    const initializeChatSession = async () => {
      if (!tenantData?.id || !hasAccess) return;
      
      try {
        // Check if there's an active session
        let activeSession = await dataService.getActiveChatSession(tenantData.id);
        
        if (!activeSession) {
          // Create new session
          const sessionId = await dataService.generateSessionId();
          const sessionData: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'> = {
            sessionId,
            title: `Chat Session ${new Date().toLocaleDateString()}`,
            messageCount: 0,
            lastMessageAt: new Date(),
            isActive: true
          };
          
          try {
            await dataService.createChatSession(tenantData.id, sessionData);
            activeSession = { id: sessionId, ...sessionData } as ChatSession;
          } catch (createError) {
            console.error('Failed to create chat session:', createError);
            return;
          }
        }
        
        setCurrentSessionId(activeSession.sessionId);
        
        // Load messages for this session
        const sessionMessages = await dataService.getChatMessages(tenantData.id, activeSession.sessionId);
        
        if (sessionMessages.length === 0) {
          // Add welcome message for new sessions
          const welcomeMessage: ChatMessage = {
            id: 'welcome',
            role: 'assistant',
            content: `👋 **Hello! I'm your AI Business Assistant for CafeLawn!**\n\nI'm here to help you understand and manage your business better. I can answer questions about:\n\n📊 **Sales & Revenue**: Today's sales, revenue trends, profit analysis\n👥 **Customer Insights**: Top customers, customer analytics, spending patterns  \n💰 **Payment Management**: Unpaid bills, payment tracking, collections\n📦 **Inventory Control**: Stock levels, low stock alerts, inventory value\n🍽️ **Menu Analytics**: Popular items, item performance, pricing insights\n📈 **Business Overview**: Overall performance, growth trends, recommendations\n\nHow can I help you today? 💼`,
            timestamp: new Date(),
            sessionId: activeSession.sessionId
          };
          
          setMessages([welcomeMessage]);
        } else {
          setMessages(sessionMessages);
        }
      } catch (error) {
        console.error('Error initializing chat session:', error);
      }
    };

    if (hasAccess && !accessLoading) {
      initializeChatSession();
    }
  }, [tenantData?.id, hasAccess, accessLoading]);

  // Speech Recognition Setup
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const rec = new SpeechRecognition();
        rec.continuous = false;
        rec.interimResults = false;
        rec.lang = 'en-US';
        
        rec.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          if (transcript) {
            setInputValue(prev => prev ? prev + ' ' + transcript : transcript);
          }
          setIsListening(false);
        };
        
        rec.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
        
        rec.onend = () => {
          setIsListening(false);
        };
        
        setRecognition(rec);
      }
    }

    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Scroll to bottom whenever messages list updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Voice dictation triggers
  const startListening = () => {
    if (recognition) {
      try {
        recognition.start();
        setIsListening(true);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const stopListening = () => {
    if (recognition) {
      try {
        recognition.stop();
        setIsListening(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Text-To-Speech (Assistant response reading)
  const toggleSpeak = (messageId: string, text: string) => {
    if (typeof window === 'undefined') return;
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (speakingMessageId === messageId) {
      synth.cancel();
      setSpeakingMessageId(null);
    } else {
      synth.cancel();
      // Strip markdown syntax for natural voice synthesis
      const cleanText = text
        .replace(/\*\*/g, '')
        .replace(/`/g, '')
        .replace(/•/g, '')
        .replace(/- /g, '')
        .replace(/#+ /g, '');
      
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.onend = () => setSpeakingMessageId(null);
      utterance.onerror = () => setSpeakingMessageId(null);
      synth.speak(utterance);
      setSpeakingMessageId(messageId);
    }
  };

  // Copy to clipboard with visual checklist tick
  const handleCopy = (messageId: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  };

  // Sample suggestions for quick access
  const quickSuggestions = [
    { icon: DollarSign, text: "What are today's sales?", category: "Sales" },
    { icon: Users, text: "Show me top customers", category: "Customers" },
    { icon: Package, text: "Which items are low on stock?", category: "Inventory" },
    { icon: TrendingUp, text: "What's our revenue trend?", category: "Analytics" },
    { icon: AlertCircle, text: "Show me unpaid customers", category: "Payments" },
    { icon: BarChart3, text: "Show me business overview", category: "Overview" }
  ];

  // Manual create session ("+ New Chat")
  const handleNewChat = async () => {
    if (!tenantData?.id) return;
    
    try {
      setIsLoading(true);
      const sessionId = await dataService.generateSessionId();
      const sessionData: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'> = {
        sessionId,
        title: `Chat ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
        messageCount: 0,
        lastMessageAt: new Date(),
        isActive: true
      };
      
      await dataService.createChatSession(tenantData.id, sessionData);
      
      setCurrentSessionId(sessionId);
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        role: 'assistant',
        content: `👋 **Hello! I'm your AI Business Assistant for CafeLawn!**\n\nI'm here to help you understand and manage your business better. I can answer questions about:\n\n📊 **Sales & Revenue**: Today's sales, revenue trends, profit analysis\n👥 **Customer Insights**: Top customers, customer analytics, spending patterns\n💰 **Payment Management**: Unpaid bills, payment tracking, collections\n📦 **Inventory Control**: Stock levels, low stock alerts, inventory value\n🍽️ **Menu Analytics**: Popular items, item performance, pricing insights\n📈 **Business Overview**: Overall performance, growth trends, recommendations\n\nHow can I help you today? 💼`,
        timestamp: new Date(),
        sessionId: sessionId
      };
      
      setMessages([welcomeMessage]);
      
      // Pull fresh sessions list
      const sessions = await dataService.getChatSessions(tenantData.id);
      setChatSessions(sessions);
    } catch (error) {
      console.error('Error creating new session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load chat session on click
  const loadChatSession = async (sessionId: string) => {
    if (!tenantData?.id) return;
    
    try {
      setIsLoading(true);
      const sessionMessages = await dataService.getChatMessages(tenantData.id, sessionId);
      
      if (sessionMessages.length === 0) {
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          role: 'assistant',
          content: `👋 **Hello! I'm your AI Business Assistant for CafeLawn!**\n\nI'm here to help you understand and manage your business better. I can answer questions about:\n\n📊 **Sales & Revenue**: Today's sales, revenue trends, profit analysis\n👥 **Customer Insights**: Top customers, customer analytics, spending patterns\n💰 **Payment Management**: Unpaid bills, payment tracking, collections\n📦 **Inventory Control**: Stock levels, low stock alerts, inventory value\n🍽️ **Menu Analytics**: Popular items, item performance, pricing insights\n📈 **Business Overview**: Overall performance, growth trends, recommendations\n\nHow can I help you today? 💼`,
          timestamp: new Date(),
          sessionId: sessionId
        };
        setMessages([welcomeMessage]);
      } else {
        setMessages(sessionMessages);
      }
      
      setCurrentSessionId(sessionId);
      if (window.innerWidth < 768) {
        setShowSessions(false); // Close sidebar on mobile
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!tenantData?.id) return;
    
    try {
      await dataService.deleteChatSession(tenantData.id, sessionId);
      setChatSessions(prev => prev.filter(session => session.sessionId !== sessionId));
      
      // If we deleted the current active session, initialize or select another
      if (sessionId === currentSessionId) {
        const remaining = chatSessions.filter(s => s.sessionId !== sessionId);
        if (remaining.length > 0) {
          loadChatSession(remaining[0].sessionId);
        } else {
          handleNewChat();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const handleSendMessage = async (queryText?: string) => {
    const textToSend = queryText || inputValue;
    if (!textToSend.trim() || isLoading || !tenantData?.id || !hasAccess) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
      sessionId: currentSessionId
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Load fresh business intelligence data
      const freshBusinessData = await businessIntelligenceService.getBusinessData(tenantData.id);
      
      // Inject session for rate limiting & intelligence caching
      const businessDataWithSession = {
        ...freshBusinessData,
        sessionId: currentSessionId
      };
      
      // Request AI response
      const aiResponse = await firebaseAIService.processBusinessQuery(
        textToSend,
        tenantData.id,
        businessDataWithSession
      );

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse.content,
        timestamp: new Date(),
        sessionId: currentSessionId
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save messages in Cloud Firestore
      if (tenantData?.id && currentSessionId) {
        try {
          await dataService.saveChatMessage(tenantData.id, {
            role: 'user',
            content: textToSend,
            timestamp: new Date(),
            sessionId: currentSessionId,
            queryType: 'business_query'
          });

          await dataService.saveChatMessage(tenantData.id, {
            role: 'assistant',
            content: aiResponse.content,
            timestamp: new Date(),
            sessionId: currentSessionId,
            queryType: 'ai_response',
            dataUsed: aiResponse.dataUsed,
            confidence: aiResponse.confidence,
            suggestions: aiResponse.suggestions
          });

          const currentMessageCount = messages.length + 2; 
          await dataService.updateChatSession(tenantData.id, currentSessionId, {
            messageCount: currentMessageCount,
            lastMessageAt: new Date()
          });

          // Refresh session list quietly
          const sessions = await dataService.getChatSessions(tenantData.id);
          setChatSessions(sessions);
        } catch (dbError) {
          console.error('Failed to store history in Firestore:', dbError);
        }
      }
    } catch (error) {
      let errorMessage = "I'm having trouble processing your request right now. Please try again in a moment.";
      
      if (error instanceof Error) {
        if (error.message.includes('API key') || error.message.includes('OpenAI')) {
          errorMessage = "I'm having trouble connecting to the AI service. Please check your API configuration.";
        } else if (error.message.includes('business data') || error.message.includes('Firebase')) {
          errorMessage = "I'm having trouble accessing your business data. Please check your connection and try again.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        sessionId: currentSessionId
      };

      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Grow textarea dynamically
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 140) + 'px';
  };

  // Render markdown tags with highly premium CSS typography
  const renderMarkdown = (text: string) => {
    if (!text) return '';
    
    // 1. Escape basic tags to prevent XSS
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
      
    // 2. Bold tags: **text**
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900 dark:text-zinc-50">$1</strong>');
    
    // 3. Inline code blocks
    html = html.replace(/`(.*?)`/g, '<code class="px-1.5 py-0.5 bg-slate-100 dark:bg-zinc-800 rounded font-mono text-xs text-indigo-600 dark:text-indigo-400 font-semibold">$1</code>');
    
    // 4. Custom blockquotes or formatted sections
    html = html.replace(/&gt; (.*?)\n/g, '<blockquote class="border-l-4 border-indigo-500 pl-4 my-2 italic text-slate-600 dark:text-zinc-400">$1</blockquote>\n');

    // 5. Line bullet points
    const lines = html.split('\n');
    let inList = false;
    const processedLines = lines.map(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
        const content = trimmed.replace(/^[•\-\*]\s*/, '').trim();
        let res = '';
        if (!inList) {
          inList = true;
          res += '<ul class="list-disc pl-5 my-3 space-y-1 text-slate-700 dark:text-zinc-300">';
        }
        res += `<li class="pl-1">${content}</li>`;
        return res;
      } else {
        let res = '';
        if (inList) {
          inList = false;
          res += '</ul>';
        }
        res += line;
        return res;
      }
    });
    
    if (inList) {
      processedLines.push('</ul>');
    }
    
    html = processedLines.join('\n');
    
    // 6. Section gaps
    html = html.replace(/\n\n/g, '</div><div class="mt-4">');
    html = html.replace(/\n/g, '<br />');
    
    return (
      <div 
        className="text-slate-800 dark:text-zinc-200 text-[15px] leading-[1.65] font-normal tracking-normal select-text break-words pr-2"
        dangerouslySetInnerHTML={{ __html: `<div class="w-full">${html}</div>` }} 
      />
    );
  };

  // Filtered session list based on search query
  const filteredSessions = chatSessions.filter(session => 
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isChatEmpty = messages.length <= 1; // Contains welcome message only or empty

  if (!tenantData) {
    return (
      <div className="h-full bg-slate-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="text-center animate-pulse">
          <div className="w-12 h-12 rounded-full bg-indigo-50 dark:bg-zinc-900 flex items-center justify-center mx-auto mb-4 border border-indigo-100 dark:border-zinc-800">
            <Bot className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h2 className="text-sm font-semibold text-slate-800 dark:text-zinc-200 mb-1">
            Loading AI Assistant
          </h2>
          <p className="text-xs text-slate-500 dark:text-zinc-400">
            Setting up your secure environment...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex min-h-0 overflow-hidden bg-slate-50 dark:bg-zinc-950 transition-all duration-300 relative ${
      isFullscreen ? 'fixed inset-0 z-50' : 'w-full'
    }`}>
      {/* LEFT SIDEBAR - Sessions History Drawer */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-80 md:static md:h-full flex flex-col 
        bg-white dark:bg-zinc-900 border-r border-slate-200 dark:border-zinc-800 shadow-xl md:shadow-none
        transition-all duration-300 ease-in-out overflow-hidden
        ${showSessions 
          ? 'translate-x-0 md:w-80 opacity-100' 
          : '-translate-x-full md:translate-x-0 md:w-0 md:border-r-0 md:opacity-0 pointer-events-none md:pointer-events-auto'
        }
      `}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-200 dark:border-zinc-800 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-bold text-slate-800 dark:text-zinc-200 uppercase tracking-wider text-xs">
                History
              </span>
            </div>
            {/* Mobile close history */}
            <button
              onClick={() => setShowSessions(false)}
              className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* New Chat CTA Button */}
          <button
            onClick={handleNewChat}
            disabled={isLoading || !hasAccess}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-50 hover:bg-indigo-100/80 active:bg-indigo-100 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 text-xs font-semibold rounded-xl transition-all duration-200 border border-indigo-100/50 dark:border-indigo-950/50 hover:scale-[0.98] active:scale-[0.96]"
          >
            <Plus className="w-4 h-4" />
            <span>New conversation</span>
          </button>

          {/* Search box to filter old chats */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Search chat..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 text-slate-700 dark:text-zinc-300 placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Sessions Scroll List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-zinc-800 scrollbar-track-transparent">
          {isLoadingSessions ? (
            <div className="p-4 text-center">
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin mx-auto" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="py-8 text-center text-slate-400 dark:text-zinc-600">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">No conversations found</p>
            </div>
          ) : (
            filteredSessions.map((session) => {
              const isActive = session.sessionId === currentSessionId;
              return (
                <div
                  key={session.sessionId}
                  onClick={() => loadChatSession(session.sessionId)}
                  className={`
                    group p-3 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200
                    ${isActive 
                      ? 'bg-slate-100/80 dark:bg-zinc-800 border-l-2 border-indigo-600 text-slate-900 dark:text-zinc-100 font-medium' 
                      : 'hover:bg-slate-50 dark:hover:bg-zinc-800/40 text-slate-600 dark:text-zinc-400'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0 pr-2">
                    <h4 className="text-[13px] truncate pr-1">
                      {session.title.replace(/Chat Session\s+/, '')}
                    </h4>
                    <p className="text-[10px] text-slate-400 dark:text-zinc-500 mt-0.5">
                      {session.messageCount || 0} messages • {session.lastMessageAt ? new Date(session.lastMessageAt).toLocaleDateString(undefined, {month: 'short', day: 'numeric'}) : ''}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(session.sessionId, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-slate-400 hover:text-red-500 transition-all duration-150"
                    title="Delete Chat"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Mobile Sidebar overlay backdrop */}
      {showSessions && (
        <div 
          onClick={() => setShowSessions(false)}
          className="fixed inset-0 bg-slate-900/20 dark:bg-black/50 z-20 md:hidden backdrop-blur-sm animate-fade-in"
        />
      )}

      {/* MAIN CHAT AREA */}
      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-zinc-950 relative">
        {/* TOP BAR / HEADER */}
        <div className="bg-white dark:bg-zinc-950 border-b border-slate-100 dark:border-zinc-900 px-4 md:px-6 py-3.5 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3">
            {/* Main application sidebar toggle */}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('toggle-main-sidebar'))}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 transition-colors"
              title="Toggle main menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-md animate-pulse-slow">
                <Bot className="w-4.5 h-4.5 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-slate-900 dark:text-zinc-100 tracking-tight flex items-center gap-1.5">
                  CafeLawn AI
                  <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                </h1>
                <p className="text-[10px] text-slate-500 dark:text-zinc-500 font-medium">
                  Google Standard Assistant
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Toggle History Button */}
            <button
              onClick={() => setShowSessions(!showSessions)}
              className={`p-2 rounded-xl transition-colors ${
                showSessions 
                  ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400' 
                  : 'hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500'
              }`}
              title="Chat History"
            >
              <History className="w-5 h-5" />
            </button>

            <button
              onClick={handleNewChat}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 transition-colors"
              title="New Chat"
            >
              <Plus className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 transition-colors hidden sm:flex"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-4.5 h-4.5" />
              ) : (
                <Maximize2 className="w-4.5 h-4.5" />
              )}
            </button>
          </div>
        </div>

        {/* MESSAGES & CHAT CANVAS */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6 min-h-0 bg-slate-50/20 dark:bg-zinc-950 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-zinc-800 scrollbar-track-transparent">
          {/* Access Control State */}
          {accessLoading ? (
            <div className="h-full flex flex-col items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-indigo-500 animate-spin mb-2" />
              <span className="text-xs text-slate-500 dark:text-zinc-400">Loading access rights...</span>
            </div>
          ) : !hasAccess ? (
            <div className="max-w-md mx-auto my-12 p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-xl text-center">
              <div className="w-12 h-12 bg-red-50 dark:bg-red-950/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100 dark:border-red-950/30">
                <Bot className="w-6 h-6 text-red-500" />
              </div>
              <h2 className="text-base font-bold text-slate-800 dark:text-zinc-200 mb-2">
                Upgrade Plan for AI Chatbot
              </h2>
              <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed mb-5">
                The CafeLawn AI Business Intelligence Chatbot is exclusively available for Professional and Enterprise subscribers.
              </p>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-100 dark:border-zinc-900 text-left text-xs mb-6 space-y-2">
                <p className="font-semibold text-slate-700 dark:text-zinc-300">Redeem premium operational capabilities:</p>
                <ul className="space-y-1.5 text-slate-500 dark:text-zinc-400">
                  <li className="flex items-center gap-1.5">📊 <strong>Advanced Analytics:</strong> Detailed daily sales and margins</li>
                  <li className="flex items-center gap-1.5">🤖 <strong>Predictive Inventories:</strong> AI alerts before items run dry</li>
                  <li className="flex items-center gap-1.5">👤 <strong>User Management:</strong> Team delegation controls</li>
                </ul>
              </div>
              <button 
                onClick={() => window.location.href = '/subscription'}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white text-xs font-semibold rounded-xl transition-all shadow-md"
              >
                Choose Subscription Plan
              </button>
            </div>
          ) : (
            <>
              {/* EMPTY STATE - Google Gemini Style Welcomer */}
              {isChatEmpty ? (
                <div className="max-w-3xl mx-auto py-10 md:py-16 flex flex-col justify-center min-h-[50vh]">
                  <div className="text-left mb-10 pl-2 animate-fade-in-up">
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 bg-clip-text text-transparent mb-3 py-1">
                      Hello, {tenantData?.businessName || 'Business Owner'}
                    </h2>
                    <h3 className="text-2xl md:text-3xl font-semibold text-slate-300 dark:text-zinc-700 leading-tight">
                      Ready to scale operations today? Ask away.
                    </h3>
                  </div>

                  {/* Suggestion cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-2 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                    {quickSuggestions.slice(0, 4).map((suggestion, index) => {
                      const Icon = suggestion.icon;
                      return (
                        <div
                          key={index}
                          onClick={() => handleSendMessage(suggestion.text)}
                          className="group p-4 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-2xl shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-zinc-700 transition-all duration-200 cursor-pointer flex flex-col justify-between hover:scale-[1.01] active:scale-[0.99] min-h-[100px]"
                        >
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-600 mb-2">
                            {suggestion.category}
                          </p>
                          <p className="text-sm text-slate-700 dark:text-zinc-200 font-medium leading-relaxed group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                            {suggestion.text}
                          </p>
                          <div className="flex items-center justify-end mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* CHAT DIALOG FLOW */
                <div className="max-w-3xl mx-auto space-y-8 pb-32">
                  {messages.map((message) => {
                    const isUser = message.role === 'user';
                    return (
                      <div
                        key={message.id}
                        className={`flex gap-4 ${isUser ? 'justify-end' : 'justify-start'} group`}
                      >
                        {/* Assistant Avatar Icon */}
                        {!isUser && (
                          <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-md flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}

                        {/* Message box */}
                        <div className={`relative ${isUser ? 'max-w-[75%]' : 'max-w-[90%] flex-1'}`}>
                          {/* User bubble style */}
                          {isUser ? (
                            <div className="bg-indigo-600 dark:bg-indigo-600 text-white rounded-3xl rounded-tr-md py-3 px-5 shadow-sm text-[15px] leading-relaxed font-normal">
                              {message.content}
                            </div>
                          ) : (
                            /* Borderless Assistant response styled directly on feed */
                            <div className="py-1">
                              {renderMarkdown(message.content)}
                              
                              {/* Utility Button Toolbar under message */}
                              <div className="flex items-center gap-3.5 mt-3 pt-2 text-slate-400 dark:text-zinc-500 border-t border-slate-100 dark:border-zinc-900 w-fit">
                                {/* Copy Button */}
                                <button
                                  onClick={() => handleCopy(message.id || '', message.content || '')}
                                  className="hover:text-slate-700 dark:hover:text-zinc-200 transition-colors p-1 rounded-lg hover:bg-slate-50 dark:hover:bg-zinc-900/60"
                                  title="Copy response"
                                >
                                  {copiedMessageId === message.id ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </button>

                                {/* Text To Speech Speaker Button */}
                                <button
                                  onClick={() => toggleSpeak(message.id || '', message.content || '')}
                                  className={`hover:text-slate-700 dark:hover:text-zinc-200 transition-colors p-1 rounded-lg hover:bg-slate-50 dark:hover:bg-zinc-900/60 ${
                                    speakingMessageId === message.id ? 'text-indigo-500 dark:text-indigo-400' : ''
                                  }`}
                                  title="Speak aloud"
                                >
                                  {speakingMessageId === message.id ? (
                                    <VolumeX className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
                                  ) : (
                                    <Volume2 className="w-3.5 h-3.5" />
                                  )}
                                </button>

                                <span className="text-[10px] text-slate-300 dark:text-zinc-700 font-mono">
                                  {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* LOADING THINKING BALLOON */}
                  {isLoading && (
                    <div className="flex gap-4 justify-start">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-md flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="py-2.5 px-4 bg-slate-50 dark:bg-zinc-900 rounded-2xl rounded-bl-sm border border-slate-100 dark:border-zinc-800 shadow-sm flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                        <span className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
                          Consulting CafeLawn DB...
                        </span>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </>
          )}
        </div>

        {/* FIXED BOTTOM INPUT WORKSPACE CONTAINER */}
        {hasAccess && !accessLoading && (
          <div className="absolute bottom-0 left-0 right-0 z-10 max-w-3xl mx-auto w-full px-4 pb-6 pt-2 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-zinc-950 dark:via-zinc-950/95">
            {/* Quick chips - horizontal scrolling list, shown above input when messages present */}
            {!isChatEmpty && (
              <div className="flex gap-2 overflow-x-auto py-2 px-1 max-w-full scrollbar-none mb-2 select-none">
                {quickSuggestions.map((suggestion, index) => {
                  const Icon = suggestion.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleSendMessage(suggestion.text)}
                      disabled={isLoading}
                      className="flex-shrink-0 flex items-center gap-1.5 py-1.5 px-3.5 bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-full text-xs text-slate-600 dark:text-zinc-300 transition-colors shadow-sm hover:scale-[0.98]"
                    >
                      <Icon className="w-3 h-3 text-slate-400" />
                      <span>{suggestion.text}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Input pill capsule container */}
            <div className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-lg border border-slate-200 dark:border-zinc-800 rounded-3xl shadow-xl p-2 pl-4 pr-3 flex items-end gap-3 transition-shadow focus-within:shadow-2xl focus-within:border-slate-300 dark:focus-within:border-zinc-700">
              <textarea
                value={inputValue}
                onChange={handleTextareaChange}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your business..."
                rows={1}
                className="flex-1 max-h-[140px] min-h-[40px] py-2.5 bg-transparent text-slate-800 dark:text-zinc-100 text-[14px] leading-relaxed placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none resize-none scrollbar-none"
                disabled={isLoading}
              />

              {/* Control Action Tools inside Capsule */}
              <div className="flex items-center gap-1.5 pb-1 flex-shrink-0">
                {/* Voice Dictation Button */}
                {recognition && (
                  <button
                    onClick={isListening ? stopListening : startListening}
                    disabled={isLoading}
                    className={`p-2.5 rounded-2xl transition-all duration-200 ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-slate-50 dark:bg-zinc-800 text-slate-500 hover:text-slate-800 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-700'
                    }`}
                    title={isListening ? 'Listening... Tap to stop' : 'Voice Dictate'}
                  >
                    {isListening ? (
                      <MicOff className="w-4 h-4" />
                    ) : (
                      <Mic className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Send Button */}
                <button
                  onClick={() => handleSendMessage()}
                  disabled={isLoading || !inputValue.trim()}
                  className="p-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-300 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-600 text-white rounded-2xl transition-all shadow-sm hover:shadow-md hover:scale-[1.03] disabled:hover:scale-100 active:scale-[0.97]"
                  title="Send query"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-300" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Sub-footer details */}
            <div className="flex justify-between items-center px-4 mt-2 text-[10px] text-slate-400 dark:text-zinc-500 select-none">
              <span>
                🔒 Cloud Firestore secured data
              </span>
              <button 
                onClick={() => setInputValue('')}
                className="hover:text-slate-600 dark:hover:text-zinc-300 transition-colors"
              >
                Clear text
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatbot;
